import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { injectIntl } from 'react-intl';
import {
  ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar,
} from 'recharts';
import {
  sum, values, isEmpty, filter, map,
} from 'lodash/fp';
import messages from '../messages';
import QuestionTitle from './QuestionTitle';
import QuestionInfo from './QuestionInfo';
import OtherAnswers from './OtherAnswers';
import ChartTooltipContent from './ChartTooltipContent';

const S = {
  QuestionInfo: styled(QuestionInfo)`
    margin-bottom: 24px;
  `,
  OtherAnswers: styled(OtherAnswers)`
    margin-top: 24px;
  `,
};

const MultipleSelectionQuestion = ({
  intl, question, stats, individuals, onUpdate,
}) => {
  const isOthersOption = type => type === 'TEXT';
  const hasOtherAnswer = !isEmpty(filter(option => option.type === 'TEXT', question.options));

  const responderCount = useMemo(() => (
    filter(data => !isEmpty(data.answers[question.number]), individuals.data)
      .length
  ), [individuals.data, question.number]);

  const answerData = useMemo(() => (map((option) => {
    const data = {};
    let value;

    if (isOthersOption(option.type)) {
      data.name = '기타';
      value = isEmpty(stats.others) ? 0 : sum(values(stats.others));
    } else {
      data.name = `${option.number}번`;
      value = stats[option.number] || 0;
    }

    data.value = ((value / responderCount) * 100).toFixed(0);
    data.selectorCount = value;

    return data;
  }, question.options)), [question, stats, responderCount]);

  const axisTickStyle = { fontSize: 12, fill: '#424242' };

  return (
    <div>
      <QuestionTitle number={question.number} onUpdate={onUpdate}>
        {question.content
          || intl.formatMessage(
            messages.multipleSelectionQuestionNoContent,
            { number: question.number },
          )}
      </QuestionTitle>
      <S.QuestionInfo>
        {`복수 선택형 / ${question.required ? '필수응답' : '선택응답'} / 응답 ${responderCount}명`}
      </S.QuestionInfo>
      <ResponsiveContainer ratio={1} height={300}>
        <BarChart
          data={answerData}
          maxBarSize={15}
          layout="vertical"
          margin={{
            top: 5, right: 5, bottom: 5, left: -10,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />

          <XAxis type="number" tick={axisTickStyle} unit="%" tickCount={6} domain={[0, 100]} />
          <YAxis dataKey="name" type="category" tick={axisTickStyle} />

          <Tooltip content={<ChartTooltipContent percentParameter={responderCount} isValuePercentage />} />

          <Bar dataKey="value" fill="#1976d2" />
        </BarChart>
      </ResponsiveContainer>
      {hasOtherAnswer
        ? <S.OtherAnswers stats={stats.others} />
        : null}
    </div>
  );
};

MultipleSelectionQuestion.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  question: PropTypes.shape({
    number: PropTypes.number.isRequired,
    type: PropTypes.oneOf([
      'SINGLE_SELECTION',
      'MULTIPLE_SELECTION',
      'DESCRIPTIVE',
      // XXX: 하위 호환성 유지를 위한 필드. 추후 제거 예정
      'SINGLE_CHOICE',
      'MULTIPLE_CHOICE',
      'SUBJECTIVE',
    ]).isRequired,
    content: PropTypes.string.isRequired,
    position: PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
    }),
    size: PropTypes.shape({
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
    }),
    required: PropTypes.bool.isRequired,
    field: PropTypes.shape({
      width: PropTypes.number,
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
    }),
    options: PropTypes.arrayOf(PropTypes.shape({
      content: PropTypes.string.isReuqired,
      number: PropTypes.number.isRequired,
      position: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
      }),
    })),
  }).isRequired,
  stats: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.number, PropTypes.objectOf(PropTypes.number),
    ]),
  ),
  individuals: PropTypes.shape({
    data: PropTypes.array,
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
};

MultipleSelectionQuestion.defaultProps = {
  stats: null,
};

export default injectIntl(MultipleSelectionQuestion);
